import {computed, inject} from '@angular/core';
import {patchState, signalStoreFeature, type, withComputed, withMethods} from '@ngrx/signals';
import {QuestionState} from '../../models/game-models.model';
import {GameFeatureInput} from './feature-input';
import {ResourcesService} from '../../services/resources.service';

export enum ScoreClassification {
  Max = 'max',
  High = 'high',
  Medium = 'medium',
  Low = 'low'
}

/**
 * Computes a score for a set of finished question states.
 * Questions that ended in `wrong-and-over` score nothing; a hint reduces the score.
 */
export function computeScoreFromStates(
  questionStates: QuestionState[],
  possibleScores: number[],
  hintReduction: number
): number {
  return questionStates.reduce((subTotal, questionState) => {
    if (questionState.feedback === 'wrong-and-over') {
      return subTotal;
    }
    const reduction = questionState.hasHadHint ? hintReduction : 0;
    return subTotal + (possibleScores[questionState.attempt - 1] - reduction);
  }, 0);
}

/**
 * Contributes score computation: the live `currentScore`, the points earned for
 * the current question, and a `computeScore` method that freezes the final score.
 */
export function withScore() {
  return signalStoreFeature(
    type<GameFeatureInput>(),
    withComputed((store, resources = inject(ResourcesService)) => {
      const maxScore = computed(() => store.possibleScores()[0] * store.currentGameQuestions().length);

      const scoreClassification = computed(() => {
        const score = store.score();
        if (score === maxScore()) return ScoreClassification.Max;
        if (score >= store.highScoreTreshold()) return ScoreClassification.High;
        if (score >= store.mediumScoreTreshold()) return ScoreClassification.Medium;
        return ScoreClassification.Low;
      });

      return {
        maxScore,
        currentScore: computed(() =>
          computeScoreFromStates(store.finishedQuestionStates(), store.possibleScores(), store.hintReduction())
        ),
        earnedPointsForCurrentQuestion: computed(() => {
          const state = store.questionState();
          const reduction = state.hasHadHint ? store.hintReduction() : 0;
          return store.possibleScores()[state.attempt - 1] - reduction;
        }),
        scoreClassification,
        scoreVerdict: computed(() => {
          const texts = resources.languageFile.value();
          switch (scoreClassification()) {
            case ScoreClassification.Max: return texts.maxScoreVerdict;
            case ScoreClassification.High: return texts.highScoreVerdict;
            case ScoreClassification.Medium: return texts.mediumScoreVerdict;
            case ScoreClassification.Low: return texts.lowScoreVerdict;
          }
        }),
      };
    }),
    withMethods((store) => ({
      /** Freezes the final score based on all finished questions. */
      computeScore(): void {
        patchState(store, {
          score: computeScoreFromStates(
            store.finishedQuestionStates(),
            store.possibleScores(),
            store.hintReduction()
          ),
        });
      },
    }))
  );
}
